import React, { useState, useEffect } from 'react';
import axios from 'axios';
// @ts-ignore
import { FetchAllCountries } from './Utils/CountryUtility';
import './TicketForm.css';

/* ============================
   Types
   ============================ */

interface TicketPayload {
  title: string;
  description: string;
  sightseeing_id: number | null;
  food_id: number | null;
}

interface TicketResponse {
  id: number;
  title: string;
  description: string;
  sightseeing: number | null;
  food: number | null;
  created_at: string;
}

interface ApiError {
  error: string;
}

interface SightseeingItem {
  id: number;
  place: string | null;
  activity: string | null;
  images: string[];
}

interface FoodItem {
  id: number;
  dish: string | null;
  restaurant: string | null;
  images: string[];
}

interface PaginatedResponse<T> {
  results: T[];
  total_pages: number;
  current_page: number;
}

interface Country {
  name: string;
  iso: string;
}

interface TicketFormProps {
  autoItemSearch?: string | null;
  autoCategory?: 'food' | 'sightseeing' | null;
  autoItemId?: number | null;
  autoIso?: string | null;
  autoCountry?: string | null;
}

/* ============================
   Component
   ============================ */

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export function TicketForm({ autoCategory, autoItemId, autoIso, autoCountry, autoItemSearch }: TicketFormProps) {
  const [countries, setCountries] = useState<Country[]>([]);
  const [countryQuery, setCountryQuery] = useState('');
  const [countrySuggestions, setCountrySuggestions] = useState<Country[]>([]);
  const [iso, setIso] = useState('');

  const [category, setCategory] = useState<'food' | 'sightseeing' | ''>('');

  const [items, setItems] = useState<(FoodItem | SightseeingItem)[]>([]);
  const [itemSearch, setItemSearch] = useState('');
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  /* ============================
     or just fill the data from the report button on a post.
     ============================ */

  // ⭐ AUTO-FILL HERE

  useEffect(() => {
    if (autoItemSearch) setItemSearch(autoItemSearch);
    if (autoIso) setIso(autoIso);
    if (autoCountry) setCountryQuery(autoCountry);
    if (autoCategory) setCategory(autoCategory);
    if (autoItemId) setSelectedItemId(autoItemId);
  }, [autoIso, autoCategory, autoItemId, autoCountry, autoItemSearch]);

  /* ============================
     Load all countries once
     ============================ */
  useEffect(() => {
    FetchAllCountries().then((data: Country[]) => {
      setCountries(data);
    });
  }, []);

  /* ============================
     Autosuggest filtering
     ============================ */
  useEffect(() => {
    if (!countryQuery) {
      setCountrySuggestions([]);
      return;
    }

    const filtered = countries.filter((c) => c.name.toLowerCase().includes(countryQuery.toLowerCase()));

    setCountrySuggestions(filtered);
  }, [countryQuery, countries]);

  const handleCountrySelect = (country: Country) => {
    setCountryQuery(country.name);
    setIso(country.iso);
    setCountrySuggestions([]);

    // Reset category + items
    setCategory('');
    setItems([]);
    setSelectedItemId(null);
    setItemSearch('');
  };

  /* ============================
     Fetch Food or Sightseeing when ISO + category selected
     ============================ */
  useEffect(() => {
    if (!iso || !category) return;

    const baseUrl = category === 'food' ? `${backendUrl}/worlddata/food/get-food-data/9/?iso=${encodeURIComponent(iso)}` : `${backendUrl}/worlddata/sightseeing/get-sightseeing-data/9/?iso=${encodeURIComponent(iso)}`;

    const fetchAllPages = async () => {
      try {
        let page = 1;
        let allResults: any[] = [];
        let totalPages = 1;

        while (page <= totalPages) {
          const separator = baseUrl.includes('?') ? '&' : '?';
          const res = await axios.get<PaginatedResponse<any>>(`${baseUrl}${separator}page=${page}`);
          allResults = [...allResults, ...res.data.results];
          totalPages = res.data.total_pages;
          page++;
        }

        setItems(allResults);
      } catch (err) {
        console.error('Failed to load items', err);
      }
    };

    fetchAllPages();
  }, [iso, category]);

  /* ============================
     Filter items by search
     ============================ */
  const filteredItems = items.filter((item) => {
    const text =
      category === 'food' ? `${(item as FoodItem).dish || ''} ${(item as FoodItem).restaurant || ''}` : `${(item as SightseeingItem).place || ''} ${(item as SightseeingItem).activity || ''}`;

    return text.toLowerCase().includes(itemSearch.toLowerCase());
  });

  /* ============================
     Submit Ticket
     ============================ */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const payload: TicketPayload = {
      title,
      description,
      sightseeing_id: category === 'sightseeing' ? selectedItemId : null,
      food_id: category === 'food' ? selectedItemId : null,
    };

    try {
      await axios.post<TicketResponse>(`${backendUrl}/worlddata/user/create-ticket/`, payload, { headers: { 'Content-Type': 'application/json' } });

      setMessage(`Ticket created! Thank you so much!`);

      // Reset
      setTitle('');
      setDescription('');
      setCountryQuery('');
      setIso('');
      setCategory('');
      setItems([]);
      setSelectedItemId(null);
      setItemSearch('');
    } catch (error: any) {
      if (error.response) {
        const err = error.response.data as ApiError;
        setMessage('Error: ' + err.error);
      } else {
        setMessage('Network error');
      }
    } finally {
      setLoading(false); // hide loader
    }
  };

  /* ============================
     UI
     ============================ */
  return (
    <div className="ticket-form-container">
      <h2>Create a Ticket</h2>

      {/* Country Search */}
      <label>Country</label>
      <input type="text" placeholder="Search for a country" value={countryQuery} onChange={(e) => setCountryQuery(e.target.value)} />

      {countrySuggestions.length > 0 && (
        <div className="country-suggestions">
          {countrySuggestions.map((c) => (
            <div key={c.iso} onClick={() => handleCountrySelect(c)} className="country-suggestion-item">
              {c.name} ({c.iso})
            </div>
          ))}
        </div>
      )}

      {/* Category Selector */}
      {iso && (
        <>
          <label>Choose Category</label>
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value as any);
              setItems([]);
              setSelectedItemId(null);
              setItemSearch('');
            }}
          >
            <option value="">Select...</option>
            <option value="food">Food</option>
            <option value="sightseeing">Sightseeing</option>
          </select>
        </>
      )}

      {/* Search + Items */}
      {category && (
        <>
          <label>Search {category}</label>
          <input value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder={`Search ${category}...`} />

          <div className="item-list">
            {filteredItems.map((item) => {
              const name = category === 'food' ? (item as FoodItem).dish || (item as FoodItem).restaurant : (item as SightseeingItem).place || (item as SightseeingItem).activity;

              const rawImage = item.images && item.images.length > 0 ? item.images[0] : null;

              return (
                <div key={item.id} onClick={() => setSelectedItemId(item.id)} className={`item-entry ${selectedItemId === item.id ? 'selected' : ''}`}>
                  {rawImage && <img src={`${backendUrl}/django-dynamic/pictures/${rawImage}`} alt={String(name || 'Selected item')} />}
                  <span>{name}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Ticket Form */}
      {selectedItemId && (
        <form onSubmit={handleSubmit}>
          <label>Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required />

          <label>Reason for the Report</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} required />

          <button onClick={handleSubmit} type="submit" disabled={loading}>
            {loading ? <div className="loader"></div> : 'Submit Ticket'}
          </button>
        </form>
      )}

      {message && <p className="ticket-message">{message}</p>}
    </div>
  );
}

/* ============================================================
   NewPostForm Component
   ============================================================ */

export const NewPostForm: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Country[]>([]);
  const [iso, setIso] = useState('');

  const [type, setType] = useState<'food' | 'sightseeing' | ''>('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  /* Load countries */
  useEffect(() => {
    FetchAllCountries().then((data: Country[]) => setCountries(data));
  }, []);

  /* Filter suggestions */
  useEffect(() => {
    if (!query) {
      setSuggestions([]);
      return;
    }

    const filtered = countries.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));

    setSuggestions(filtered);
  }, [query, countries]);

  const selectCountry = (country: Country) => {
    setQuery(country.name);
    setIso(country.iso);
    setSuggestions([]);
  };

  /* Submit new post */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); // show loader

    const payload = {
      country: query,
      type,
      name,
      description,
    };

    try {
      await axios.post(`${backendUrl}/worlddata/user/create-new-post/`, payload, { headers: { 'Content-Type': 'application/json' } });

      setMessage('Thanks for contributing! Your post has been submitted.');

      setQuery('');
      setIso('');
      setType('');
      setName('');
      setDescription('');
    } catch (error) {
      setMessage('Something went wrong while submitting your post.');
    } finally {
      setLoading(false); // hide loader
    }
  };

  return (
    <div className="ticket-form-container" >
      <h2 className="share-spot-title">Share a Travel Spot with Us</h2>
      <p className="share-spot-description" style={{ color: 'var(--text-muted)' }}>
        Help us improve the travel experience. If you know a hidden gem, a local food place, or an underrated sightseeing location, submit a new post and share it with the community.
      </p>

      <label>Country</label>
      <input type="text" placeholder="Search for a country" value={query} onChange={(e) => setQuery(e.target.value)} />

      {suggestions.length > 0 && (
        <div className="country-suggestions">
          {suggestions.map((c) => (
            <div key={c.iso} onClick={() => selectCountry(c)} className="country-suggestion-item">
              {c.name} ({c.iso})
            </div>
          ))}
        </div>
      )}

      {iso && (
        <>
          <label>Type</label>
          <select value={type} onChange={(e) => setType(e.target.value as any)}>
            <option value="">Select...</option>
            <option value="food">Food</option>
            <option value="sightseeing">Sightseeing</option>
          </select>
        </>
      )}

      {type && (
        <>
          <label>Name</label>
          <input type="text" placeholder="e.g. Hidden Waterfall, Grandma's Kitchen" value={name} onChange={(e) => setName(e.target.value)} />

          <label>Description</label>
          <textarea placeholder="Tell us why this place is special..." value={description} onChange={(e) => setDescription(e.target.value)} />

          <button onClick={handleSubmit} type="submit" disabled={loading}>
            {loading ? <div className="loader"></div> : 'Submit Post'}
          </button>
        </>
      )}

      {message && <p className="ticket-message">{message}</p>}
    </div>
  );
};
